import { useCallback, useMemo, useState, type RefObject } from 'react';
import type { Editor } from '@tiptap/react';
import type { MediaRow } from '../../api/client';
import type { ImagePickerApi, PickTarget } from './image-picker-context';
import { insertImage, replaceImage, uploadReplaceImage } from './image-commands';

type UploadImage = (f: File) => Promise<{ url: string }>;

const TITLES: Record<PickTarget['mode'], string> = {
  insert: '從媒體庫插入照片',
  replace: '從媒體庫挑一張替換這張圖',
};

/**
 * 內文圖片的「插入／替換」狀態與動作：只有一個 MediaPickerDrawer，
 * 由 Tiptap.tsx 持有，工具列與每張圖的浮動工具列都透過 context 呼叫這裡。
 */
export function useInlineImages(
  editorRef: RefObject<Editor | null>,
  insertFiles: (editor: Editor, files: File[]) => void,
  uploadImage: UploadImage,
  onImageInserted?: () => void,
) {
  const [open, setOpen] = useState(false);
  // Drawer 關閉動畫期間仍保留上一個 target，標題才不會閃一下
  const [target, setTarget] = useState<PickTarget>({ mode: 'insert' });

  // 加圖／換圖都是「重量級」動作：立刻存檔，不等自動儲存的 debounce。
  // setTimeout 0：等 onUpdate 造成的 state 更新 commit 後再存，才存得到新內容。
  const saveSoon = useCallback(() => {
    if (onImageInserted) setTimeout(onImageInserted, 0);
  }, [onImageInserted]);

  const api = useMemo<ImagePickerApi>(
    () => ({
      openLibrary: (t) => {
        setTarget(t);
        setOpen(true);
      },
      uploadInsert: (files) => {
        if (editorRef.current) insertFiles(editorRef.current, files);
      },
      uploadReplace: (getPos, file) => {
        if (editorRef.current) void uploadReplaceImage(editorRef.current, getPos, file, uploadImage, saveSoon);
      },
    }),
    [editorRef, insertFiles, uploadImage, saveSoon],
  );

  const pick = useCallback(
    (m: MediaRow) => {
      const editor = editorRef.current;
      if (!editor) return;
      const input = { src: m.url, alt: m.alt };
      if (target.mode === 'insert') insertImage(editor, input);
      else if (!replaceImage(editor, target.getPos, input)) return;
      saveSoon();
    },
    [editorRef, target, saveSoon],
  );

  return {
    api,
    drawer: { open, title: TITLES[target.mode], onClose: () => setOpen(false), onPick: pick },
  };
}
